/**
 * Calculates pagination range string for API requests.
 * @param {number} page - Zero-based page number
 * @param {number} itemsPerPage - Items per page
 * @returns {string} Range string formatted as "startIndex, endIndex"
 */
export function getItemRange(page, itemsPerPage) {
  const startIndex = page * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return `${startIndex}, ${endIndex - 1}`;
}

/**
 * Range string that requests every item for the current result set.
 * @param {number} totalItems
 * @returns {string}
 */
export function getAllItemsRange(totalItems) {
  if (!totalItems || totalItems <= 0) {
    return "0, 0";
  }
  return `0, ${totalItems - 1}`;
}

/**
 * Derive consistent pagination fields from a Spring Data Page JSON body.
 * totalPages uses ceil(totalElements / pageSize) so the UI matches the API total count.
 *
 * @param {object|null|undefined} response - Parsed JSON body (not a raw array)
 * @param {number} [itemsPerPageFallback]
 * @returns {{ totalPages: number, totalElements: number, currentPage: number, pageSize: number }|null}
 */
export function normalizeSpringDataPage(response, itemsPerPageFallback) {
  if (!response || Array.isArray(response)) {
    return null;
  }
  const totalElements =
    response.totalElements != null ? response.totalElements : null;
  const pageSize =
    itemsPerPageFallback != null
      ? itemsPerPageFallback
      : (response.pageable?.pageSize ?? 10);
  if (pageSize <= 0) {
    return null;
  }
  const totalPages =
    totalElements == null || totalElements === 0
      ? 0
      : Math.ceil(totalElements / pageSize);
  let currentPage = response.pageable?.pageNumber ?? 0;
  if (totalPages === 0) {
    currentPage = 0;
  } else {
    currentPage = Math.min(Math.max(0, currentPage), totalPages - 1);
  }
  return { totalPages, totalElements, currentPage, pageSize };
}
